import React, { useState } from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";

function telegramLinkFromHandleOrUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const h = s.replace(/^@/, "");
  return h ? `https://t.me/${h}` : "";
}

/** Defaults match public Telegram assets; env vars override when set (build / deploy). */
/** Support admin chat — do not use REACT_APP_TELEGRAM_URL here (often the public channel). */
const TELEGRAM_SUPPORT_URL =
  (process.env.REACT_APP_TELEGRAM_SUPPORT_URL || "").trim() ||
  "https://t.me/AlphaSniperAdmin";
const TELEGRAM_COMMUNITY_URL =
  (process.env.REACT_APP_TELEGRAM_COMMUNITY_URL || "").trim() ||
  "https://t.me/AlphaSniperCommunity";
const TELEGRAM_CHANNEL_URL =
  (process.env.REACT_APP_TELEGRAM_CHANNEL_URL || "").trim() ||
  telegramLinkFromHandleOrUrl(process.env.REACT_APP_TELEGRAM_CHANNEL || "") ||
  "https://t.me/AlphaSniperChannel";

const TICKET_CATEGORIES = [
  { value: "onboarding", label: "Onboarding & setup" },
  { value: "technical", label: "Technical / copy-trading" },
  { value: "billing", label: "Billing & subscription" },
  { value: "account", label: "Account & login" },
  { value: "other", label: "Other" },
];

const telegramCardClass =
  "flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-brand-400/40 hover:bg-white/[0.06]";

/**
 * Public contact page — layout inspired by https://unitedkings.net/contact-us/
 * AlphaSniper copy.
 */
export default function ContactUs() {
  const [ticketName, setTicketName] = useState("");
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketCategory, setTicketCategory] = useState("other");
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketStatus, setTicketStatus] = useState("idle");
  const [ticketFeedback, setTicketFeedback] = useState(null);

  async function submitTicket(e) {
    e.preventDefault();
    setTicketStatus("sending");
    setTicketFeedback(null);
    try {
      const res = await fetch(apiUrl("/api/contact-ticket"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: ticketName,
          email: ticketEmail,
          category: ticketCategory,
          subject: ticketSubject,
          message: ticketMessage,
        }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok || !data.ok) {
        const text = clientSafeMessage(
          data.message,
          "Could not send your ticket. Please try again.",
        );
        setTicketFeedback({
          type: "error",
          text,
        });
        setTicketStatus("idle");
        return;
      }
      setTicketFeedback({ type: "success", text: data.message });
      setTicketSubject("");
      setTicketMessage("");
      setTicketStatus("idle");
    } catch {
      setTicketFeedback({
        type: "error",
        text: "Network error. Check your connection and try again.",
      });
      setTicketStatus("idle");
    }
  }

  const inputClass =
    "mt-1.5 w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none ring-brand-400/0 transition focus:border-brand-400/50 focus:ring-2 focus:ring-brand-400/30";
  const labelClass = "block text-xs font-semibold uppercase tracking-wide text-gray-400";

  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="mb-5 space-y-2 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Get in touch</h1>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Reach our team for onboarding, billing, or technical questions about your slot and
            copy-trading setup. We aim to keep responses clear and actionable—especially around
            account linking and risk settings.
          </p>
        </header>

        <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
          <h2 className="text-xl font-bold sm:text-2xl">Open a support ticket</h2>
          <p className="mt-2 text-sm text-gray-400">
            Send us a message below. It opens a tracked request for our team—include your account
            e-mail if the issue is about billing or login.
          </p>

          {ticketFeedback ? (
            <div
              className={
                ticketFeedback.type === "success"
                  ? "mt-4 rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100"
                  : "mt-4 rounded-xl border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              }
              role="status"
            >
              {ticketFeedback.text}
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={submitTicket} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="ticket-name">
                  Your name
                </label>
                <input
                  id="ticket-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  minLength={2}
                  maxLength={200}
                  value={ticketName}
                  onChange={(ev) => setTicketName(ev.target.value)}
                  className={inputClass}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="ticket-email">
                  E-mail
                </label>
                <input
                  id="ticket-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={ticketEmail}
                  onChange={(ev) => setTicketEmail(ev.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="ticket-category">
                Topic
              </label>
              <select
                id="ticket-category"
                name="category"
                value={ticketCategory}
                onChange={(ev) => setTicketCategory(ev.target.value)}
                className={`${inputClass} cursor-pointer appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-10`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                }}
              >
                {TICKET_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} className="bg-[#0B1437] text-white">
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass} htmlFor="ticket-subject">
                Subject
              </label>
              <input
                id="ticket-subject"
                name="subject"
                type="text"
                required
                minLength={4}
                maxLength={180}
                value={ticketSubject}
                onChange={(ev) => setTicketSubject(ev.target.value)}
                className={inputClass}
                placeholder="Short summary of your request"
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="ticket-message">
                Message
              </label>
              <textarea
                id="ticket-message"
                name="message"
                required
                minLength={20}
                maxLength={8000}
                rows={6}
                value={ticketMessage}
                onChange={(ev) => setTicketMessage(ev.target.value)}
                className={`${inputClass} resize-y min-h-[140px]`}
                placeholder="Describe what you need help with (include relevant details, no passwords)."
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={ticketStatus === "sending"}
                className="inline-flex rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ticketStatus === "sending" ? "Sending…" : "Send ticket"}
              </button>
              <span className="text-xs text-gray-500">
                By submitting, you agree we use this information only to respond to your request.
              </span>
            </div>
          </form>
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-bold sm:text-2xl">Get support</h2>
          <p className="mt-2 text-sm text-gray-400">
            Choose the channel that fits your request. If you are already logged in, you can also
            use the in-app help area when available.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <a
              href={TELEGRAM_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={telegramCardClass}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-300">
                Telegram support
              </span>
              <span className="mt-2 text-lg font-semibold text-white">AlphaSniper Support</span>
              <span className="mt-2 text-sm text-gray-400">
                Direct line for onboarding, billing, and account-related questions.
              </span>
            </a>

            <a
              href={TELEGRAM_COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={telegramCardClass}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-300">
                Telegram community
              </span>
              <span className="mt-2 text-lg font-semibold text-white">Discussion group</span>
              <span className="mt-2 text-sm text-gray-400">
                Chat with other members—use support for private or account-specific issues.
              </span>
            </a>

            <a
              href={TELEGRAM_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={telegramCardClass}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-300">
                Telegram channel
              </span>
              <span className="mt-2 text-lg font-semibold text-white">Announcements</span>
              <span className="mt-2 text-sm text-gray-400">
                Official updates and news—read-only channel.
              </span>
            </a>
          </div>
        </section>

        <section className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <h2 className="text-lg font-bold">Response time</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-300">
            We typically reply within <strong className="text-white">one business day</strong> for
            email or form requests. Instant messengers may be faster when staffed, but are not a
            substitute for regulated advice or trade-by-trade instructions.
          </p>
          <p className="mt-3 text-xs text-gray-500">
            AlphaSniper does not provide personalised investment advice; we help with product and
            onboarding topics within our published policies.
          </p>
        </section>

        <section className="mt-8 rounded-2xl border border-brand-500/25 bg-brand-500/10 p-8 text-center">
          <h2 className="text-lg font-bold sm:text-xl">Looking for answers?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-300">
            Many billing, refund, and setup topics are covered in our FAQ before you open a ticket.
          </p>
          <Link
            to="/FrequentlyAskedQuestions"
            className="mt-6 inline-flex rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white hover:bg-brand-600"
          >
            View FAQ
          </Link>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
